import React, { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { useDispatch, useSelector } from "react-redux";

import { useAbility } from "../../hooks";
import Card from "../../components/Card";
import Grid, { Col, Row } from "../../components/Grid";
import Button from "../../components/Button";
import SectionLabel from "../../components/SectionLabel";
import {
  paymentInformation,
  profile,
  subscriptions,
} from "../../config/router/routes";
import { ACTION, SUBJECT } from "../../constants/permissions";
import { THIRD_PARTY_OFFERING } from "../../services/api/subscription/types";
import ProfileCard from "../../components/ProfileCard";
import { selectOriginSettings } from "../../store/metadata";
import {
  fetchBillingData,
  selectBillingData,
  selectCustomerData,
  selectPrincipalData,
  selectRecurlyBillingAccount,
} from "../../store/user";
import { selectCurrentSubscriptionDetail } from "../../store/subscriptions";
import { usePaymentType } from "../../hooks/use-payment-type";
import { ActiveSubscription } from "../Subscriptions/components/ActiveSubscription/ActiveSubscription";
import { DownloadApps } from "../../components/DownloadApp/DownloadApp";
import Icon from "../../components/Icon";
import { IconName } from "../../components/Icon/types";
import { usePaymentIcon } from "../../hooks/use-payment-icon";

import "./styles.scss";

export const Account: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const profileData = useSelector(selectPrincipalData);
  const customerData = useSelector(selectCustomerData);
  const activeSubscription = useSelector(selectCurrentSubscriptionDetail);
  const isCorporate = customerData?.is_corporate;
  const ability = useAbility();
  const offeringCode = activeSubscription?.offeringCode || "";
  const isPaymentSubscriptionsVisible =
    !THIRD_PARTY_OFFERING.includes(offeringCode) &&
    (ability.can(ACTION.READ, SUBJECT.SUBSCRIPTION) ||
      ability.can(ACTION.WRITE, SUBJECT.BILLING));
  const isActiveSubscriptionsVisible = !THIRD_PARTY_OFFERING.includes(
    offeringCode,
  );
  const recurlyBillingAccount = useSelector(selectRecurlyBillingAccount);
  const billingInfo = useSelector(selectBillingData);

  const originSettings = useSelector(selectOriginSettings);
  const isEmailAvailable =
    originSettings?.principal_email_synchronized_from_username === false;

  const _data = {
    password: "• • • • • • • • • •",
    accountLevel: "Premium",
    currentConnection: "None",
    natFirewall: "Enabled",
  };

  const paymentMethod = usePaymentType({
    billingInfo,
    spCode: activeSubscription
      ? activeSubscription?.sp.sp_code
      : recurlyBillingAccount?.sp_code,
  });
  const paymentMethodIconName = usePaymentIcon(billingInfo);

  const cardNumber = billingInfo?.payment_info?.last_four
    ? `•••• •••• •••• ${billingInfo?.payment_info?.last_four}`
    : null;

  const goToProfile = () => {
    history.push(profile.path);
  };

  const goToPaymentInformation = () => {
    history.push(paymentInformation.path);
  };

  const goToSubscriptions = () => {
    history.push(subscriptions.path);
  };

  const fetchUserBillingData = ({
    spCode,
    customerId,
  }: {
    spCode?: string;
    customerId?: string;
  }) => {
    if (spCode && customerId) {
      dispatch(
        fetchBillingData({
          customerId,
          spCode,
        }),
      );
    }
  };

  useEffect(() => {
    fetchUserBillingData({
      spCode: recurlyBillingAccount?.sp_code,
      customerId: profileData?.customer_id,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recurlyBillingAccount, profileData]);

  return (
    <section className="ams-account">
      <h1>{t("account_overview")}</h1>

      <ProfileCard
        showPassword
        isShowEmail={isEmailAvailable}
        titleText={t("profile")}
        actionText={t("viewProfile")}
        name={profileData?.name}
        businessName={isCorporate ? customerData?.name : ""}
        email={profileData?.email}
        password={_data.password}
        username={profileData?.username}
        brandCode={profileData?.tenant_code}
        handleAction={goToProfile}
      />
      {isPaymentSubscriptionsVisible && (
        <>
          <Card>
            <h2>{t("paymentInformation")}</h2>
            <Grid>
              <Row>
                <Col>
                  <SectionLabel>{t("paymentMethod")}</SectionLabel>
                </Col>
                <Col className="ams-col__value">
                  <SectionLabel className={"flex flex-align-center"}>
                    {paymentMethod ? (
                      <>
                        <Icon className="ams-payment-icon">
                          {paymentMethodIconName as IconName}
                        </Icon>
                        <span className="ams-payment-type">
                          {t(paymentMethod)}
                        </span>
                        &nbsp;
                        {cardNumber}
                      </>
                    ) : (
                      t("none")
                    )}
                  </SectionLabel>
                </Col>
              </Row>
            </Grid>
            <Button onClick={goToPaymentInformation}>
              {t("viewPaymentInformation")}
            </Button>
          </Card>
        </>
      )}

      {activeSubscription && isActiveSubscriptionsVisible && (
        <Card>
          <ActiveSubscription
            data={activeSubscription}
            billingInfo={billingInfo}
            isAccountOverviewTab
          />
          <Button onClick={goToSubscriptions}>
            {t("manage_subscriptions")}
          </Button>
        </Card>
      )}

      <Card>
        <DownloadApps />
      </Card>
    </section>
  );
};
